import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import { turso } from '@/database/client';
import { validatePasswordStrength } from '@/lib/auth';

export default function AdminPasswordResetPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const adminAuth = sessionStorage.getItem('whipt_admin_auth');
    const dev = localStorage.getItem('whipt_dev_mode') === 'true';
    if (adminAuth === 'true' || dev) return;
    navigate('/admin/sign-in', { replace: true });
  }, [navigate]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !password || !confirm) {
      setError('All fields are required');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    const strength = validatePasswordStrength(password);
    if (!strength.isValid) {
      setError(strength.errors.join('\n'));
      return;
    }

    try {
      setLoading(true);
      // find user
      const lookup = await turso.execute({
        sql: 'SELECT id FROM users WHERE email = ? LIMIT 1',
        args: [email]
      });

      if (!lookup.rows || lookup.rows.length === 0) {
        setError('No user found for that email');
        return;
      }

      const userId = lookup.rows[0].id as string;
      const hash = await bcrypt.hash(password, 12);

      await turso.execute({
        sql: 'UPDATE users SET password_hash = ?, last_active_at = datetime("now") WHERE id = ?',
        args: [hash, userId]
      });

      // invalidate existing sessions
      await turso.execute({
        sql: 'DELETE FROM sessions WHERE user_id = ?',
        args: [userId]
      });

      setMessage('Password reset and sessions cleared. User must sign in again.');
      setPassword('');
      setConfirm('');
    } catch (err: any) {
      console.error('[AdminPasswordReset] Failed:', err);
      setError('Failed to reset password. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#0b0720] to-[#120d2e] flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-xl space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-white">Admin: Manual Password Reset</h1>
          <p className="text-slate-400 text-sm">Set a new password directly for any user email. Existing sessions are revoked.</p>
        </div>

        {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm whitespace-pre-line">{error}</div>}
        {message && <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-200 text-sm">{message}</div>}

        <form className="space-y-4" onSubmit={handleReset}>
          <label className="block space-y-1">
            <span className="text-sm text-slate-300">User Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
              required
              disabled={loading}
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm text-slate-300">New Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
              required
              disabled={loading}
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm text-slate-300">Confirm Password</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
              required
              disabled={loading}
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold disabled:opacity-60"
          >
            {loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>

        <div className="text-xs text-slate-500">
          Requires admin auth (or dev mode). Resets Turso password_hash and clears sessions.
        </div>
      </div>
    </div>
  );
}